/**
 * Waveform downsampling for the voice-message bubble UI.
 *
 * The bubble renders a 64-bar amplitude chart. We pre-compute the
 * buckets on the *sender* side so the receiver never has to decode the
 * Opus stream to draw the bar chart, and so the bytes are covered by
 * the outer envelope signature — a hostile server cannot ship a
 * misleading waveform.
 *
 * Threat surface trimmed by this file:
 *   - Output is always exactly `bucketCount` bytes. Empty or shorter
 *     inputs are padded/upscaled rather than producing a variable-
 *     length blob a peer could exploit as a side-channel.
 *   - Normalisation is deterministic: same float input → same byte
 *     output. Tested explicitly so a fingerprinted recorder cannot
 *     "watermark" the waveform stochastically.
 *   - silenceRatio() drives the auto-stop heuristic. It is a pure
 *     function — no MediaRecorder, no AudioContext — so the policy can
 *     be exhaustively tested with synthetic samples.
 *
 * Pure-logic only: no Web Audio API, no side effects.
 * Mirrors `kaether_for_ios/Stoat/E2EE/VoiceMessageWaveform.swift`.
 */

export const WAVEFORM_BUCKET_COUNT = 64 as const;

/**
 * Partition `samples` into `targetBuckets` roughly-equal windows,
 * compute the RMS amplitude per window, then scale so the loudest
 * bucket is 255. Returns exactly `targetBuckets` bytes. Edge cases:
 *
 *   - Empty samples → all-zero output.
 *   - Fewer samples than buckets → each input sample fans out
 *     across consecutive buckets so the output is still exactly
 *     `targetBuckets` bytes (avoids a short waveform leaking
 *     recording length).
 *   - All-silence input → all-zero output (no division-by-zero
 *     in normalisation).
 */
export function downsample(
  samples: Float32Array | number[],
  targetBuckets: number = WAVEFORM_BUCKET_COUNT,
): Uint8Array {
  const n = Math.max(1, targetBuckets);

  if (samples.length === 0) {
    return new Uint8Array(n); // all zeros
  }

  const rms = new Float64Array(n);

  if (samples.length >= n) {
    // Standard path: compute RMS amplitude per window.
    const total = samples.length;
    for (let bucket = 0; bucket < n; bucket++) {
      const start = Math.floor((bucket * total) / n);
      const endRaw = Math.floor(((bucket + 1) * total) / n);
      const end = Math.min(Math.max(endRaw, start + 1), total); // at least 1 sample per bucket
      let sumSq = 0;
      let count = 0;
      for (let i = start; i < end; i++) {
        const v = samples[i]!;
        sumSq += v * v;
        count++;
      }
      if (count > 0) {
        rms[bucket] = Math.sqrt(sumSq / count);
      }
    }
  } else {
    // Upscaling path: spread the few samples we have evenly across
    // the bucket array so the output length is stable.
    for (let bucket = 0; bucket < n; bucket++) {
      const idx = Math.min(
        Math.floor((bucket * samples.length) / n),
        samples.length - 1,
      );
      rms[bucket] = Math.abs(samples[idx]!);
    }
  }

  // Normalise so the loudest bucket maps to 255. All-silence input
  // has peak=0; short-circuit to avoid division-by-zero.
  let peak = 0;
  for (let i = 0; i < n; i++) {
    if (rms[i]! > peak) peak = rms[i]!;
  }
  if (peak === 0) {
    return new Uint8Array(n); // all zeros
  }

  const out = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    const scaled = (rms[i]! / peak) * 255.0;
    out[i] = Math.min(255, Math.max(0, Math.round(scaled)));
  }
  return out;
}

/**
 * Fraction of samples whose absolute amplitude is below
 * `silenceThreshold`. Returned value is in [0, 1]. Drives the
 * auto-stop-on-silence policy — Signal-style, prevents accidental
 * long recordings of nothing.
 *
 * Empty input → 1.0 (treat no data as fully silent).
 */
export function silenceRatio(
  samples: Float32Array | number[],
  silenceThreshold: number = 0.01,
): number {
  if (samples.length === 0) return 1.0;
  let quiet = 0;
  for (let i = 0; i < samples.length; i++) {
    if (Math.abs(samples[i]!) < silenceThreshold) {
      quiet++;
    }
  }
  return quiet / samples.length;
}

/** Namespace export matching the iOS enum style used in the rest of this module. */
export const VoiceMessageWaveform = {
  bucketCount: WAVEFORM_BUCKET_COUNT,
  downsample,
  silenceRatio,
} as const;
